import axios from 'axios';
import localStorageService from '../services/LocalStorageService';

/**
 * Interceptors for API requests and responses
 * Handles authentication, token refresh, and error handling
 */

// Helper function to clear auth data and redirect to login
const handleLogout = () => {
  localStorageService.clearAuthData();
  window.location.href = '/login';
};

// Setup interceptors for the API instance
const setupInterceptors = (api, API_BASE_URL) => {
  // Add request interceptor for authentication
  api.interceptors.request.use(
    (config) => {
      // Add JWT token for authentication (for authenticated endpoints)
      const token = localStorageService.getAuthToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add response interceptor to handle token refresh and expiry
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 Unauthorized responses
      if (error.response?.status === 401) {
        const errorData = error.response.data;
        
        // Check for specific token error codes
        const isTokenInvalid = 
          errorData?.code === 'token_not_valid' || 
          errorData?.detail?.includes('token') ||
          errorData?.detail === 'Authentication credentials were not provided.' ||
          errorData?.detail === 'Token is blacklisted';

        // If token is invalid/expired and we haven't tried to refresh yet
        if (isTokenInvalid && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorageService.getRefreshToken();
            
            if (refreshToken) {
              // Try to refresh the access token
              const response = await axios.post(`${API_BASE_URL}/api/auth/refresh/`, {
                refresh: refreshToken
              });

              if (response.data?.access) {
                // Save new access token
                localStorageService.setAuthToken(response.data.access);
                
                // Update the original request with new token
                originalRequest.headers['Authorization'] = `Bearer ${response.data.access}`;
                
                // Retry the original request
                return api(originalRequest);
              }
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            
            // Refresh token is also expired or invalid - logout user
            handleLogout();
            return Promise.reject(refreshError);
          }
        } else {
          // Token refresh already failed or blacklisted token - logout user
          handleLogout();
        }
      }

      return Promise.reject(error);
    }
  );
};

export default setupInterceptors;
